import { Checkbox, Input } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import type { SelectedValues } from "./index.d";
import { GuestType, Payment, Price, Seat, SeatType } from "@/graphql/types";
import Button from "../common/button/button";
import { isMobile } from "@/lib/ts/device";
import Parallelogram from "../common/commonIcon/parallelogram";
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar";
import SelectItemV2 from "../common/input/selectItemV2";

type PriceCounter = {
  seatType: number;
  guestType: number;
  name: string;
  price: number;
  count: number;
};

type Props = {
  onConfirmed: (usePoint: number, finalPrice: number, payment: number) => Promise<void>;
  agreements: string[];
  prices: Price[];
  guestTypes: GuestType[];
  seatTypes: SeatType[];
  changedSeats: Seat[];
  selectedValues: SelectedValues;
  ownPoint: number;
  priceListIfSeatSelectDisabled: { [guestType: number]: number };
  seatSelectDisabled: Boolean;
  payments: Payment[]
};

const Confirm = (props: Props) => {
  const [checked, setChecked] = useState(props.agreements.map(() => false));
  const [usePoint, setUsePoint] = useState(0);
  const [payment, setPayment] = useState(-1)
  const [pushed, setPushed] = useState(false)

  const snackBar = useSnackBar({})  

  const priceInfo = useMemo(() => {
    let totalPrice = 0;
    const priceList: PriceCounter[] = [];

    Object.keys(props.selectedValues).forEach((guestType) => {
      const value = props.selectedValues[parseInt(guestType)];

      const guestTypeName =
        props.guestTypes?.find((type) => type.id === parseInt(guestType))
          ?.name || "一般";
      Object.keys(value).forEach((key) => {
        const seatId = value[parseInt(key)];
        const seat = props.changedSeats?.find((seat) => seat.id === seatId);

        const seatType = seat ? seat.type : 0;
        const seatTypeName =
          props.seatTypes?.find((type) => type.id === seatType)?.name || "一般";

        const price =
          props.prices?.find(
            (priceObj) =>
              priceObj.guestType === parseInt(guestType) &&
              priceObj.seatType === seatType,
          )?.price || 0;
        totalPrice += price;
        const counter = priceList?.find(
          (c) => c.guestType === parseInt(guestType) && c.seatType === seatType,
        );
        if (counter) {
          counter.count = counter.count + 1;
        } else {
          const priceCounter: PriceCounter = {
            name: `${guestTypeName} - ${seatTypeName}`,
            guestType: parseInt(guestType),
            seatType,
            count: 1,
            price,
          };
          priceList.push(priceCounter);
        }
      });
    });
    return {
      priceList,
      totalPrice,
    };
  }, [
    props.changedSeats,
    props.guestTypes,
    props.prices,
    props.seatTypes,
    props.selectedValues,
  ]);

  const changeUsePoint = useCallback(
    (event: any) => {
      let point = event.target.value;

      // 合計額マイナスになる場合は0円に補正
      if (point > priceInfo.totalPrice) {
        event.target.value = priceInfo.totalPrice;
        point = priceInfo.totalPrice;
      }
      // 所持ポイントを超えないよう補正
      if (point > props.ownPoint) {
        event.target.value = props.ownPoint;
        point = props.ownPoint;
      }
      setUsePoint(point);
    },
    [priceInfo.totalPrice, props.ownPoint],
  );

  const onChecked = useCallback(
    (index: number) => {
      const checkedCopy = [...checked];
      checkedCopy[index] = !checkedCopy[index];

      setChecked(checkedCopy);
    },
    [checked],
  );

  const checkConfirmed = useCallback(async () => {
    snackBar.loadStart()
    const checkedCopy = [...checked];
    if (
      checkedCopy.filter((checked) => checked).length === checkedCopy.length
    ) {
      const submitPrice = priceInfo.totalPrice - usePoint;
      await props.onConfirmed(usePoint, submitPrice, payment);
      snackBar.onMutationSuccess()
    } else {
      alert("注意事項を全て確認し、同意の場合はチェックしてください。");
      snackBar.onMutationError()
    }
  }, [checked, payment, priceInfo.totalPrice, props, snackBar, usePoint]);

  const onPaymentSelected = useCallback((item: Payment) => {
    setPayment(item.id || -1)
  }, [])

  const ticketDetail = useMemo(() => {
    return props.seatSelectDisabled
      ? Object.keys(props.priceListIfSeatSelectDisabled).map((key) => {
          const guestType = parseInt(key);
          const guestTypeName =
            props.guestTypes?.find((type) => type.id === guestType)?.name ||
            "一般";
          const price =
            props.prices?.find(
              (priceElm) =>
                priceElm.guestType === guestType && priceElm.seatType === 0,
            )?.price || 0;

          return (
            <div className="tickets-wrapper">
              <Parallelogram
                bodyColor="white"
                shadowColor="red"
                padding={[0, 0, 0, 40]}
              >
                <div className="tickets">
                  <div className="typeName">{guestTypeName}</div>
                  <div className="price">{price > 0 ? price + '円' : ''}</div>
                  <div className="ticketCount">
                    {props.priceListIfSeatSelectDisabled[guestType]}枚
                  </div>
                </div>
              </Parallelogram>
            </div>
          );
        })
      : Object.keys(priceInfo.priceList).map((key) => (
          <div className="ticketsParallelogram">
            <Parallelogram
              bodyColor="white"
              shadowColor="red"
              padding={[0, 0, 0, 40]}
            >
              <div className="ticketsFlex">
                <div className="tickets">
                  <div className="typeName">
                    {priceInfo.priceList[parseInt(key)].name}
                  </div>
                  <div className="price">
                    {priceInfo.priceList[parseInt(key)].price > 0 ? priceInfo.priceList[parseInt(key)].price + '円' : ''}
                  </div>
                  <div className="ticketCount">
                    {priceInfo.priceList[parseInt(key)].count}枚
                  </div>
                </div>
              </div>
            </Parallelogram>
          </div>
        ));
  }, [
    priceInfo.priceList,
    props.guestTypes,
    props.priceListIfSeatSelectDisabled,
    props.prices,
    props.seatSelectDisabled,
  ]);

  const selectedPaymentIndex = useMemo(() => {
    return props.payments.findIndex(p => p.id === payment)
  }, [payment, props.payments])

  const component = useMemo(() => {
    return (
      <ConfirmStyle>
        {
          props.agreements.length ? (
            <div className="agreements-description">
              注意事項をお読みいただき、確認・同意いただけた場合はチェックを入れてください。
            </div>
          ) : <></>
        }
        {props.agreements.map((agreement, index) => (
          <div className="confirm" key={`agreement-${index}`}>
            <div className="agreement">{agreement}</div>
            <div className="checkBox" onClick={() => onChecked(index)}>
              <div className="checkBoxBody">
                <Checkbox checked={checked[index]} />
              </div>
              <div className="text">確認・同意しました。</div>
            </div>
          </div>
        ))}
        <div className="ticketDetail">
          <div className="ticketTitle">チケット内訳</div>
          {ticketDetail}
          {priceInfo.totalPrice > 0 ? <div className="totalPrice">合計 {priceInfo.totalPrice}円</div> : <></>}
        </div>
        {
          props.ownPoint > 0 ? (
            <div className="usePoint">
              <div className="pointTitle">使用ポイント</div>
              <div className="pointParallelogram">
                <Parallelogram
                  bodyColor="white"
                  shadowColor="red"
                  padding={[5, 0, 0, 80]}
                >
                  <div className="pointInput">
                    <Input
                      sx={{ color: "black", width: "60px" }}
                      type="number"
                      defaultValue={0}
                      placeholder="0"
                      onChange={(event) => {
                        changeUsePoint(event);
                      }}
                    />
                    ポイント
                  </div>
                </Parallelogram>
              </div>
              <div className="own-points">保有: {props.ownPoint}ポイント</div>
              <div className="finalPriceDetail">
                <div className="finalPriceTitle">最終支払金額</div>
                <div className="finalPrice">
                  {priceInfo.totalPrice - usePoint}円
                </div>
              </div>
            </div>
          ) : <></>
        }
        {
          priceInfo.totalPrice - usePoint > 0 ? (
            <div className="payment">
              <div className="paymentTitle">支払方法</div>
              <div className="paymentSelect">
                <SelectItemV2
                  items={props.payments}
                  selectedIndex={selectedPaymentIndex}
                  changeIndex={(item, _) => { onPaymentSelected(item) }}
                  width={250}
                />
              </div>
            </div>
          ) : <></>
        }
        <Button
           onClick={checkConfirmed}
           label="予約を送信する"
           disabled={(priceInfo.totalPrice - usePoint > 0 && payment === -1) || pushed || checked.some(c => !c)}
           buttonType={isMobile() ? "default-red" : "powerful-red"}
        />
        {snackBar.component}
      </ConfirmStyle>
    );
  }, [changeUsePoint, checkConfirmed, checked, onChecked, onPaymentSelected, payment, priceInfo.totalPrice, props.agreements, props.ownPoint, props.payments, pushed, selectedPaymentIndex, snackBar.component, ticketDetail, usePoint]);

  return component;
};

export default Confirm;

const ConfirmStyle = styled.div`
  background-color: white;
  width: 90%;
  padding: 8px 8px 8px 8px;
  margin: 10px auto 10px auto;
  .agreements-description {
    color: black;
    font-weight: bold;
    text-align: center;
    margin: 10px auto 10px auto;
  }
  .tickets-wrapper {
    margin: 0px auto 20px auto;
  }
  .confirm {
    color: black;
    margin: 10px 0 20px 0;
    .agreement {
      color: white;
      font-weight: bold;
      text-align: center;
      background-color: black;
      margin: 0 auto 0 auto;
      padding: 20px 8px 20px 8px;
      white-space: pre-line;
    }
    .checkBox {
      height: 50px;
      width: 80%;
      margin: auto;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      .checkBoxBody {
        display: flex;
        align-items: center;
        height: 20px;
      }
      .text {
        display: flex;
        align-items: center;
        height: 20px;
      }
    }
  }
  .ticketDetail {
    width: 100%;
    margin: 10px auto 10px auto;
    padding: 10px 10px 10px 10px;
    color: white;
    background-color: black;
    .ticketTitle {
      margin-bottom: 20px;
      font-weight: bold;
    }
    .ticketsParallelogram {
      margin: 20px auto 20px auto;
    }
      .tickets {
        position: relative;
        width: 100%;
        height: 40px;
        margin: 10px auto 10px auto;
        display: flex;
        align-items: center;
        gap: 10px;
        background-color: white;
        color: black;
        z-index: 1;
        .typeName {
          width: 160px;
        }
        .price {
          width: 80px;
        }
        .ticketCount {
          width: 60px;
        }
      }
    .totalPrice {
      width: 120px;
      margin: 10px auto 0 auto;
    }
  }
  .usePoint {
    width: 100%;
    margin: 20px auto 10px auto;
    padding: 10px 10px 10px 10px;
    color: white;
    background-color: black;
    .pointTitle {
      margin-bottom: 10px;
      font-weight: bold;
    }
    .pointParallelogram {
      margin: 20px auto 20px auto;
    }
    .point-input-wrapper {
      width: 280px;
      margin: auto;
      .pointInput {
        width: 100%;
        text-align: center;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 40px;
        margin: 10px auto 10px auto;
        color: black;
        background-color: white;
        clip-path: polygon(0% 100%, 5% 0%, 100% 0%, 95% 100%);
        z-index: 1;
      }
    }
    .own-points {
      margin: auto;
      text-align: center;
    }
    .finalPriceDetail {
      width: 100%;
      text-align: center;
      margin: 20px auto 10px auto;
      .finalPriceTitle {
      }
      .finalPrice {
        width: 120px;
        text-align: center;
        font-weight: bold;
        color: white;
        margin: 0 auto 0 auto;
      }
    }
  }
  .payment {
    background-color: black;
    padding: 10px 10px 10px 10px;
    height: 120px;
    margin: 10px auto 10px auto;
    .paymentTitle {
      font-weight: bold;
    }
    .paymentSelect {
      border-radius: 8px;
      height: 50px;
      display: flex;
      background-color: white;
      align-items: center;
      justify-content: center;
      margin: 8px auto 0 auto;
    }
  }
  .submit {
    border-radius: 8px;
    margin: auto;
    width: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: black;
    height: 35px;
    cursor: pointer;
    :hover {
      background-color: gray;
    }
  }
  .ticket-detail-wrapper {
    width: 280px;
  }
  .clip-path-shadow {
    position: absolute;
    width: 280px;
    height: 40px;
    background-color: red;
    transform: translate(5px, -45px);
    clip-path: polygon(0% 100%, 5% 0%, 100% 0%, 95% 100%);
    z-index: 0;
  }
`;
